"use client"

import { Trans } from "@lingui/react/macro"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { useCallback, useEffect, useState } from "react"
import { CheckMarkIcon } from "../../icons/check-mark"
import { CopyIcon } from "../../icons/copy"

interface Props {
  copyValue: string
  children: React.ReactNode
  asChild?: boolean
  className?: string
  label?: string
  tooltipOnlyOnCopy?: boolean
  withIcon?: boolean
}

export async function copyToClipboard(value: string) {
  navigator.clipboard.writeText(value)
}

export function Copy(props: Props) {
  const [hasCopied, setHasCopied] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const copy = useCallback(() => {
    setHasCopied(true)
    setIsTooltipOpen(true)
    copyToClipboard(props.copyValue)
  }, [props.copyValue])

  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        setHasCopied(false)
        setIsTooltipOpen(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [hasCopied])

  return (
    <Tooltip
      delayDuration={100}
      open={isTooltipOpen}
      onOpenChange={(open) => {
        if (!props.tooltipOnlyOnCopy) {
          setIsTooltipOpen(open)
          return
        }

        if (!open) {
          setIsTooltipOpen(false)
        }
      }}
    >
      <TooltipTrigger
        className="cursor-pointer"
        onClick={copy}
        asChild={props.asChild}
      >
        <span className={cn("flex items-center gap-1", props.className)}>
          <span>{props.children}</span>
          {props.withIcon &&
            (hasCopied ? (
              <CheckMarkIcon className="h-4 w-4" />
            ) : (
              <CopyIcon className="h-4 w-4 text-foreground/60" />
            ))}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {hasCopied ? (
          <Trans>Copied!</Trans>
        ) : (
          <Trans>{props.label ?? "Copy"}</Trans>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
